import { makeAutoObservable, runInAction } from 'mobx'
import { getUrlEndPath } from 'shared/lib'
import { Box, type IDropdownItem, Status, Typography } from 'shared/ui'
import { type Integration, IntegrationKey } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  ChatbotBookingType,
  type ChatbotBooking,
  type IBookingType,
  type IChatbotField,
} from 'entities/Chatbot'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'
import { MeetingsStore } from 'widgets/Meetings/store/meetingsStore'
import type { ICalendlyIntegrationMeetingCustom } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/types'
import type { IEventLinkDropdownItem } from 'pages/chatbot/pages/agentBookAppointment/pages/connectCalendar/store/connectCalendar'
import { BookingFieldsStore } from 'pages/chatbot/store/bookingFieldsStore'
import { buildSectionInstruction } from 'pages/chatbot/ui/UnifiedAgent/lib/buildSectionInstruction'

type IBookingOrigin = {
  bookingEnabled: boolean
  type: IBookingType
  link: ChatbotBooking['link']
  smartBookingEnabled: boolean
  provider: ChatbotBooking['provider']
}

export class BookingSectionStore {
  isLoading = false
  connectionError = ''
  eventLinkError = ''
  bookingFields: IChatbotField[] = []
  origin: IBookingOrigin | null = null
  private _selectedIntegration: Integration | null = null
  private _integrationMap: Map<number | string, Integration> = new Map()
  private _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()
  private _meetingsStore: MeetingsStore
  private _bookingFieldsStore: BookingFieldsStore

  constructor(readonly entities: IEntitiesStore, private _booking: ChatbotBooking) {
    this._meetingsStore = new MeetingsStore({ contact: null }, this.entities)
    this._bookingFieldsStore = new BookingFieldsStore(this.entities)
    makeAutoObservable(this)
    this.init()
  }

  get bookingFieldsStore() {
    return this._bookingFieldsStore
  }

  get bookingEnabled() {
    return this._booking.bookingEnabled
  }

  get bookingType() {
    return this._booking.type
  }

  get smartBookingEnabled() {
    return this._booking.smartBookingEnabled
  }

  get isConnectionSelected() {
    return !!this._selectedIntegration
  }

  get isCalendlySelected() {
    return this._selectedIntegration?.key === IntegrationKey.calendly
  }

  get isHubspotSelected() {
    return this._selectedIntegration?.key === IntegrationKey.hubspot
  }

  isHubspotConnection = (connection: IDropdownItem): boolean => {
    const integration = this._integrationMap.get(connection.id)
    return integration?.key === IntegrationKey.hubspot
  }

  get selectedConnectionItem(): IDropdownItem | null {
    if (!this._selectedIntegration) return null
    return this.connectionList.find((item) => item.id === this._selectedIntegration?.id) ?? null
  }

  get eventLink(): IEventLinkDropdownItem | null {
    const link = this._booking.link
    if (!link) return null
    return {
      id: link.id,
      label: link.name,
      labelRight: getUrlEndPath(link.value),
      labelRightVariant: 'body-md-regular',
      data: {
        id: link.id,
        name: link.name,
        scheduling_url: link.value,
      },
    }
  }

  get isMeetingLoading() {
    return this._meetingsStore.loading
  }

  get search() {
    return this._meetingsStore.search
  }

  get connectionList(): IDropdownItem[] {
    const list: IDropdownItem[] = []

    this.entities.integrationsStore
      .getIntegrations([IntegrationKey.calendly, IntegrationKey.hubspot])
      .forEach((item) => {
        this._integrationMap.set(item.id, item)
        const isCalendly = item.key === IntegrationKey.calendly

        const label = item.name

        list.push({
          id: item.id,
          iconL: isCalendly ? 'calendly' : 'hubspot',
          label,
          labelRight: item.connected ? null : 'Connect',
          labelRightVariant: 'body-md-regular',
          labelContent: (
            <Box display={'flex'} alignItems={'center'} gap={1}>
              <Typography variant={'body-md-regular'} ariaLabel={'hubspot'}>
                {label}
              </Typography>
              {!isCalendly && (
                <Status title={'New'} intent={'purple'} emphasis={'high'} size={'label'} />
              )}
            </Box>
          ),
        })
      })

    return list
  }

  get meetingsList(): IDropdownItem[] {
    return (
      this._meetingsStore.filteredMeetings?.map((meeting) => {
        const calendarEvent: ICalendlyIntegrationMeetingCustom = {
          name: meeting.name,
          scheduling_url: 'scheduling_url' in meeting ? meeting.scheduling_url : '',
          id: meeting.id,
        }
        const isConfigurationValid =
          'is_configuration_valid' in meeting ? meeting?.is_configuration_valid : false
        const labelIntegration = this.isCalendlySelected ? 'Calendly' : 'HubSpot'

        return {
          id: meeting.id,
          label: calendarEvent.name,
          labelRight: getUrlEndPath(calendarEvent.scheduling_url),
          labelRightVariant: 'body-md-regular',
          data: calendarEvent,
          ...(!isConfigurationValid && {
            disabled: true,
            tooltipProps: {
              label: (
                <>
                  {`This link is missing required fields in ${labelIntegration}`}.
                  <br />
                  {`Update it in ${labelIntegration} to use.`}
                </>
              ),
              placement: 'top',
              breakWord: true,
            },
          }),
        }
      }) ?? []
    )
  }

  get sectionInstruction() {
    if (!this.origin) return ''
    return buildSectionInstruction('Booking', this.origin.bookingEnabled, this.bookingEnabled)
  }

  get hasChanges() {
    if (!this.origin) return false
    return (
      this.bookingEnabled !== this.origin.bookingEnabled ||
      this.bookingType !== this.origin.type ||
      this.smartBookingEnabled !== this.origin.smartBookingEnabled ||
      this.eventLink?.id !== this.origin.link?.id
    )
  }

  init = async () => {
    this.origin = {
      bookingEnabled: this.bookingEnabled,
      type: this.bookingType,
      link: this._booking.link,
      smartBookingEnabled: this.smartBookingEnabled,
      provider: this._booking.provider,
    }

    if (this._booking.link && this._booking.provider) {
      const integration = this.entities.integrationsStore.getIntegration(this._booking.provider)
      if (integration) {
        const integrationChanged = this._selectedIntegration?.id !== integration.id
        this._selectedIntegration = integration
        this._integrationMap.set(integration.id, integration)
        if (integrationChanged) {
          this._meetingsStore.onSelectedIntegration(
            integration,
            integration.key !== IntegrationKey.calendly
          )
        }
        if (this.bookingEnabled) {
          await this._loadBookingFields(this._booking.link.id, IntegrationKey[integration.key])
        }
      }
    }
  }

  enhancePayload = (isEdit: boolean) => {
    const origin = !isEdit && this.origin
    return {
      book_appointment_enabled: origin ? origin.bookingEnabled : this.bookingEnabled,
      book_appointment_type: origin ? origin.type : this.bookingType,
    }
  }

  setBookingType = (type: IBookingType) => {
    this._booking.setType(type)
  }

  setSmartBookingEnabled = (value: boolean) => {
    this._booking.setSmartBookingEnabled(value)
  }

  setConnection = async (connection: IDropdownItem, isWidget = false) => {
    const integration = this._integrationMap.get(connection.id)
    if (!integration) return

    if (!integration.connected) {
      runInAction(() => {
        this.isLoading = true
      })
      try {
        await this._manageIntegrationConnectionStore.connect(integration)
      } finally {
        runInAction(() => {
          this.isLoading = false
        })
      }
      return
    }

    if (!isWidget) {
      if (this._selectedIntegration?.id === integration.id) return

      runInAction(() => {
        if (this._selectedIntegration) {
          this._booking.setLink(null)
          this._booking.setProvider(null)
          this.entities.mergeFieldStore.removeDynamicMergeFields(
            IntegrationKey[this._selectedIntegration.key]
          )
          this._bookingFieldsStore.dispose()
          this.bookingFields = []
        }
        this._selectedIntegration = integration
        this.connectionError = ''
        if (
          integration.key === IntegrationKey.hubspot &&
          this._booking.type === ChatbotBookingType.ShareLink
        ) {
          this._booking.setType(ChatbotBookingType.TimeChat)
        }
      })
    }

    this._meetingsStore.onSelectedIntegration(
      integration,
      integration.key !== IntegrationKey.calendly
    )
  }

  isIntegrationReadyForWidget = (connection: IDropdownItem): boolean => {
    const integration = this._integrationMap.get(connection.id)
    return !!integration?.connected
  }

  fetchWidgetBookingFields = async (connection: IDropdownItem, link: IDropdownItem) => {
    const integration = this._integrationMap.get(connection.id)
    if (!integration?.connected || !integration.key) return []

    const widgetFieldsStore = new BookingFieldsStore(this.entities)
    await widgetFieldsStore.fetchFields({
      params: { id: String(link.id), integrationKey: IntegrationKey[integration.key] },
    })

    return widgetFieldsStore.fields
  }

  setLink = (link: IDropdownItem) => {
    const item = link as IEventLinkDropdownItem

    this._booking.setLink({
      value: item.data.scheduling_url,
      id: String(item.data.id),
      name: item.data.name,
    })
    this._booking.setProvider(this._selectedIntegration?.key ?? null)

    this.eventLinkError = ''

    if (this._selectedIntegration?.key) {
      void this._loadBookingFields(String(item.id), IntegrationKey[this._selectedIntegration?.key])
    }
  }

  setSearch = (value: string) => {
    this._meetingsStore.setSearch(value)
  }

  private _loadBookingFields = async (linkId: string, integrationKey: IntegrationKey) => {
    await this._bookingFieldsStore.fetchFields({ params: { id: linkId, integrationKey } })
    runInAction(() => {
      this.bookingFields = this._bookingFieldsStore.fields
    })
    this.entities.mergeFieldStore.addMergeFieldsIntegrations(
      integrationKey,
      this._bookingFieldsStore.hubspotMergeFields
    )
  }

  setBookingEnabled = (value: boolean) => {
    this._booking.setEnabled(value)

    if (!value) {
      if (this._selectedIntegration) {
        this.entities.mergeFieldStore.removeDynamicMergeFields(
          IntegrationKey[this._selectedIntegration?.key]
        )
        this._selectedIntegration = null
      }
      this.bookingFields = []
      this._bookingFieldsStore.dispose()
      this.connectionError = ''
      this.eventLinkError = ''
    }
  }

  private _setConnectionErrors = () => {
    if (!this.isConnectionSelected) {
      this.connectionError = 'Select your booking app'
    } else {
      this.eventLinkError = 'Select an event to share with contacts'
    }
  }

  handleBookingLinkError = (error: string) => {
    if (error.endsWith('field is required.')) {
      this._setConnectionErrors()
    } else {
      this.connectionError = error
    }
  }

  resetError = (key: 'connectionError' | 'eventLinkError') => {
    this[key] = ''
  }

  validateSection = () => {
    if (!this.bookingEnabled) return true
    if (!this.isConnectionSelected || !this.eventLink) {
      this._setConnectionErrors()
      return false
    }
    return true
  }

  resetSection = () => {
    if (!this.origin) return
    this.setBookingEnabled(this.origin.bookingEnabled)
    this.setBookingType(this.origin.type)
    this.setSmartBookingEnabled(this.origin.smartBookingEnabled)

    if (this.origin.link && this.origin.provider) {
      this._booking.setLink(this.origin.link)
      this._booking.setProvider(this.origin.provider)
      const integration = this.entities.integrationsStore.getIntegration(this.origin.provider)
      if (integration) {
        runInAction(() => {
          this._selectedIntegration = integration
        })
        if (this.origin.bookingEnabled) {
          void this._loadBookingFields(this.origin.link.id, IntegrationKey[integration.key])
        }
      }
    }
  }

  dispose = () => {
    this.entities.mergeFieldStore.removeMergeFieldsIntegrations(IntegrationKey.calendly)
    this.entities.mergeFieldStore.removeDynamicMergeFields(IntegrationKey.hubspot)
  }
}
